/**
 * 店舗の詳細ページのルートコンポーネント
 *
 * mainjsによって初期化され、テンプレートはJinjaの以下と紐づく
 * templates/organizations/{...}/point.html > templates/_default/point_ssr.html
 */

import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import PointService from "@/services/PointService.ts"
import Page from "@/services/Page.js"
import { Filters as _Filters } from "@/services/Filters.ts"
import { GeohashManager } from '@/services/Geohash.js'
import api from '@/api.ts'
import { deepClone } from "@/core/common.js"
import { WindowSize } from '@/constants'

import Breadcrumb from '@/components/common/Breadcrumb.vue'
import Filters from '@/components/common/Filters.vue'
import PointAttribute from '@/components/common/PointAttribute.vue'
import GoogleMapForPoint from "@/components/point/GoogleMapForPoint.vue"
import Stations from '@/components/point/Stations.vue'
import SocialActions from '@/components/point/SocialActions.vue'
import Points from '@/components/common/Points.vue'
import { parse, parseMessage } from "@/utils/format.ts"
import Recaptcha from '@/utils/recaptcha.ts'


export default {
  components: {Breadcrumb, GoogleMapForPoint, PointAttribute, Points, Filters, Stations, SocialActions},
  setup() {
    const store = useStore()

    /**
     * ウィンドウサイズ監視時のデバウンス設定
     *
     * ウィンドウサイズが変わった時に挙動を変更する必要がある処理のため、幅の監視を行う
     * ウィンドウ幅が変更され、ブレイクポイントが変わるとstoreの値を更新させる
     */
    let resizeTimer = null
    const interval = Math.floor(1000 / 60 * 10)

    /**
     * マップの初期状態を設定する
     * onMountedの前に完了させておく必要がある
     */

    // 初期表示状態
    const isHideMap = ref(false)

    // 開閉ボタンの有無
    const useOpenButton = ref(false)

    // SP判定のため、windowサイズを最新化
    store.dispatch('onWindowResized')
    
    // 地図表示の状態を更新
    setInitialMapCondition()


    onMounted(async () => {
      watchWindowSize()

      if (storelocator.point.stations.enabled) {
        // 近隣駅を取得
        await store.dispatch('updateStations', storelocator.point.point)
      }

      if (isHideMap.value) {        
        try {
          // 近隣店は地図表示に基づいて計算されるため、非表示の場合は別途計算
          await fetchNearPointsWithoutMap()
        } catch (e) {
          console.warn('[point] failed to fetch near points without map', e)
        }
      } else {
        _Filters.cache.updateLocation()
      }
    })

    function watchWindowSize() {
      store.dispatch('onWindowResized')

      window.addEventListener("resize", () => {
        if (resizeTimer !== null) {
          clearTimeout(resizeTimer)
        }
        resizeTimer = setTimeout(() => {
          store.dispatch('onWindowResized')
        }, interval)
      })
    }

    /**
     * 近隣店を選択時
     * @param {Object} point Point
     */
    function onNearPointClicked(point) {
      location.href = Page.getPointUrl(point)
    }

    /**
     * JINJA2のテンプレートで利用するGoogleMapsへのリンクURLを生成
     * settingsの設定を見て名前検索か、緯度経度検索いずれかのURLを返す
     * @param {String|null} [name] 店舗名（省略時はpointの店舗名を使用）
     * @param {Number|null} [lat] 緯度（省略時はpointの店舗の緯度を使用）
     * @param {Number|null} [lng] 経度（省略時はpointの店舗の経度を使用）
     * @return {String} GoogleマップのURL（設定が無効の場合は空文字列）
     */
    const googlemapsLink = (name, lat, lng) => {
      // GoogleマップのURL生成が無効な場合は空文字列を返す
      if (!storelocator.point.google_map_opener?.enabled) {
        return ''
      }

      // 引数が省略された場合は現在の店舗情報を使用
      const _name = name || storelocator.point.point.name
      const _lat = lat || storelocator.point.point.latitude
      const _lng = lng || storelocator.point.point.longitude

      // 設定に基づいてクエリパラメータを構築
      const searchMode = storelocator.point.google_map_opener?.by
      const query = searchMode === 'name' 
        ? `query=${encodeURIComponent(_name)}` 
        : `query=${_lat}%2C${_lng}`

      // GoogleマップのURLを返す
      return `https://www.google.com/maps/search/?api=1&${query}`
    };


    /**
     * Googleマップへのリンク（名称・住所）
     * @param {String} address 住所
     * @param {String} name 名称
     */
    const googlemapsLinkByAddress = computed(() => {
      return (address, name) => {
        return Page.getGoogleMapsLink(null, null, `${address} ${name}`)
      }
    })

    /**
     * true: 凡例を表示
     */
    const isShowFiltersHint = computed(() => {
      return storelocator.point?.filters?.help?.show
    })

    /**
     * 近隣店
     *
     * PointService.pointsのうち必要なものを抱える
     */
    const nearPoints = ref([])

    let nearPointsInitialized = false

    /**
     * 地図の初期表示状態の判定を行い、isHideMap、useOpenButton フラグを更新する
     */
    function setInitialMapCondition() {
      // 地図非表示判定 - ASN
      const hideAsns = storelocator?.point?.google_maps?.hide?.asn || []
      const headerAsns = (storelocator?.headers?.["Sl-As"] ?? "").split(" ").filter(Boolean)
      const isMatchedAsn = hideAsns.some(asn => headerAsns.includes(asn) || asn === "all")

      if (isMatchedAsn) {
        // この条件で非表示となった場合は地図を表示ボタンは表示しない
        isHideMap.value = true
        return
      }
      
      const hideSettings = storelocator?.point?.google_maps?.initial_hide || {}

      if (hideSettings?.enabled) {
        if (hideSettings?.sp_only) {
          isHideMap.value = store.state.windowSize === WindowSize.SMALL
        } else {
          isHideMap.value = true
        }

        if (hideSettings?.open_button) {
          useOpenButton.value = true
        }
      }
    }    

    /**
     * ジオハッシュ配列からポイントを取得する
     * 地図非表示時の近隣店取得用
     * 
     * @param {string[]} geohashes ジオハッシュ文字列配列
     * @returns {Promise<Object[]>} ポイント配列
     */
    async function fetchPointsByGeohash(geohashes) {
      const points = []
      for (const gh of geohashes) {
        let apiResult
        try {
          apiResult = await api.fetchPointsByGeohash(gh)
          if (apiResult?.status != 200) {
            console.warn(`[point] nearPointsSearch failed: geohash=${gh}, status=${apiResult?.status}`)
            continue
          }

          points.push(...apiResult.data.items)
        } catch (e) {
          console.warn(`[point] nearPointsSearch failed: geohash=${gh}`, e)
        }
      }

      return points
    }

    /**
     * 地図を表示しない場合でも近隣店舗を取得する
     * @returns {Promise<void>}
     */
    async function fetchNearPointsWithoutMap() {
      if (!storelocator.point?.nearby?.enabled) {
        return
      }

      const point = storelocator.point.point
      if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) {
        return
      }

      const { spherical } = await google.maps.importLibrary("geometry")
      const geohashManager = new GeohashManager(null)
      geohashManager.depth = storelocator.map?.points?.geohash?.digits || 3

      const pointLatLng = new google.maps.LatLng(point.latitude, point.longitude)
      const OFFSET = 1500

      const n = spherical.computeOffset(pointLatLng, OFFSET, 0)
      const e = spherical.computeOffset(pointLatLng, OFFSET, 90)
      const s = spherical.computeOffset(pointLatLng, OFFSET, 180)
      const w = spherical.computeOffset(pointLatLng, OFFSET, 270)

      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(s.lat(), w.lng()),
        new google.maps.LatLng(n.lat(), e.lng()),
      )

      const geohashes = geohashManager.getGeohashs(bounds)
      const points = await fetchPointsByGeohash(geohashes)
      const sortedPoints = PointService.sortByDistance(
        points,
        new google.maps.LatLng(point.latitude, point.longitude)
      )

      const NO_DISTANCE = -1
      let maxDistanceKm = NO_DISTANCE
      if (storelocator.point?.nearby?.max?.enabled && storelocator.point?.nearby?.max?.distance > 0) {
        maxDistanceKm = storelocator.point?.nearby?.max?.distance
      }

      nearPoints.value = sortedPoints.filter((p, i) => {
        if (i > 5) {
          return false
        }

        if (maxDistanceKm !== NO_DISTANCE) {
          const pointDistanceKm = p.distance / 1000
          if (pointDistanceKm > maxDistanceKm) {
            return false
          }
        }

        return p.key !== point.key
      })
      nearPointsInitialized = true
    }

    /**
     * 地図を表示する
     */
    function showMapByUser() {
      storelocator.analytics.client?.send('ShowMapByUser', {})
      isHideMap.value = false      
    }

    // 近隣店
    PointService.afterLoadPoints = (points) => {
      // 並び替える際に距離を埋め込むので、隔離しておく
      const _points = deepClone(points)
      if (nearPointsInitialized) {
        return
      }

      const point = storelocator.point.point

      // 先に並べる
      const sortedPoints = PointService.sortByDistance(
        _points,
        new google.maps.LatLng(
          point.latitude,
          point.longitude
        )
      )

      const NO_DISTANCE = -1
      let maxDistanceKm = NO_DISTANCE

      // max.distanceが 0 より大きい数値の場合その値を採用
      // それ以外(0, null, undefined, NaN, etc.)は、全て NO_DISTANCE として扱う
      if (storelocator.point?.nearby?.max?.enabled && storelocator.point?.nearby?.max?.distance > 0) {
        maxDistanceKm = storelocator.point?.nearby?.max?.distance
      }

      // 表示する店舗に絞る
      const filteredPoints = sortedPoints.filter((p, i) => {
        // 近いものの先頭、最大6店舗に絞る
        if (i > 5) {
          return false
        }

        // NO_DISTANCE の場合は距離制限をかけない
        if (maxDistanceKm !== NO_DISTANCE) {
          // p.distanceはメートルなので、キロメートルに変換
          const pointDistanceKm = p.distance / 1000

          // maxDistanceKmより遠い店舗は非表示
          if (pointDistanceKm > maxDistanceKm) {
            return false
          }
        }

        // 店舗ページのポイントは除く
        // NOTE: GoogleMapForPoint.vueにて、PointService.filters.excluedsに店舗ページのポイントが既に含まれているので不要？
        return p.key !== point.key
      })

      nearPoints.value = filteredPoints

      nearPointsInitialized = true
    }

    /**
     * 店舗
     */
    const point = computed(() => {
      return storelocator.point.point
    })

    /**
     * 店舗のフォーマット
     */
    const pointFormats = computed(() => {
      return storelocator.point?.formats
    })

    /**
     * 近くの店舗のフォーマット
     */
    const nearPointFormats = computed(() => {
      return storelocator.point?.nearby?.points?.point?.formats
    })

    /**
     * 近くの店舗の設定
     */
    const nearPointSearchConditions = ref(storelocator.point?.nearby?.points?.point?.search_conditions)

    /**
     * 表記
     */
    const messages = computed(() => {
      return storelocator.messages
    })
    const isRecaptchaEnabled = computed(() => Recaptcha.isEnabled)

    /**
     * メッセージまわり
     */
    const persistentMessage = ref({
      enabled: storelocator.point?.persistent_message?.enabled || false,
      message: parseMessage(storelocator.point?.persistent_message?.message || ''),
    })

    /**
     * メッセージ（地図の下）まわり
     */
    const persistentMessageAfterMap = ref({
      enabled: storelocator.point?.persistent_message_after_map?.enabled || false,
      message: parseMessage(storelocator.point?.persistent_message_after_map?.message || ''),
    })

    /**
     * 近隣駅まわり
     */
    const stations = ref({
      enabled: storelocator.point?.stations?.enabled || false,
      max: {
        enabled: storelocator.point?.stations?.max?.enabled,
        distance: storelocator.point?.stations?.max?.distance
      }
    })

    /**
     * ポイントのタイトルのふりがな設定
     * NOTE: point_ssr.htmlではjinja側で設定されているので不使用
     */
    const pointTitleKana = storelocator.point_title_kana || { enabled: false }

    /**
     * Pointの属性のラベルを取得
     * @param {String} label design.point.formats[].label
     * @returns {String} 挿入・変換を済ませたラベル
     */
    function getPointAttributeLabel(label, key) {
      if (!label) {
        return key
      }

      return parse({}, {
        format: label
      })
    }

    /**
     * Pointの属性の値に書式を適用したものを取得
     * @param {Object} format design.point.formats[].format
     * @returns {String} 挿入・変換を済ませた値, 表示対象外ならnull
     */
    function getPointAttributeValue(format) {
      // console.debug('getPointAttributeValue', format.key, parse(point.value, format))

      const value = parse(point.value, format)
      if (value === '' && format.hide_on_empty) {
        return null
      }

      return value
    }

    const rootPage = Page.rootPage

    return {
      messages,
      isRecaptchaEnabled,
      point,
      rootPage,
      pointFormats,
      getPointAttributeLabel,
      getPointAttributeValue,
      isShowFiltersHint,
      onNearPointClicked,
      nearPointFormats,
      nearPoints,
      nearPointSearchConditions,
      googlemapsLink,
      googlemapsLinkByAddress,
      persistentMessage,
      persistentMessageAfterMap,
      stations,
      pointTitleKana,
      isHideMap,
      useOpenButton,
      showMapByUser,
    }
  }
}
